/**
 * Client-side Validation Helpers
 *
 * These mirror the validation rules in @church-aware/shared but are
 * implemented in JavaScript for the web app. They provide immediate
 * feedback to users before submitting to Cloud Functions.
 *
 * Note: Server-side validation in Cloud Functions is still the source
 * of truth. These are for UX only.
 */

// Validation constants (mirroring @church-aware/shared)
const PHONE_VALIDATION = {
  MIN_DIGITS: 7,
  MAX_DIGITS: 15,
};

const YEAR_VALIDATION = {
  MIN_YEAR: 1800,
  getMaxYear: (offset = 5) => new Date().getFullYear() + offset,
};

const VALIDATION_PATTERNS = {
  EMAIL: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
  STATE_CODE: /^[A-Za-z]{2}$/,
  ZIPCODE: /^\d{5}(-\d{4})?$/,
  DIGITS_ONLY: /\D/g,
};

/**
 * Clean phone number to digits only
 * @param {string} phone
 * @returns {string}
 */
export function cleanPhone(phone) {
  if (!phone) return '';
  return phone.replace(VALIDATION_PATTERNS.DIGITS_ONLY, '');
}

/**
 * Validate phone number
 * @param {string} phone
 * @param {Object} options
 * @returns {{ isValid: boolean, error?: string }}
 */
export function validatePhone(phone, options = {}) {
  const { required = false } = options;

  if (!phone || phone.trim().length === 0) {
    if (required) {
      return { isValid: false, error: 'Phone number is required.' };
    }
    return { isValid: true };
  }

  const digits = cleanPhone(phone);
  if (digits.length < PHONE_VALIDATION.MIN_DIGITS || digits.length > PHONE_VALIDATION.MAX_DIGITS) {
    return {
      isValid: false,
      error: `Phone number must contain between ${PHONE_VALIDATION.MIN_DIGITS} and ${PHONE_VALIDATION.MAX_DIGITS} digits.`,
    };
  }

  return { isValid: true };
}

/**
 * Validate email address
 * @param {string} email
 * @param {Object} options
 * @returns {{ isValid: boolean, error?: string }}
 */
export function validateEmail(email, options = {}) {
  const { required = false } = options;

  if (!email || email.trim().length === 0) {
    if (required) {
      return { isValid: false, error: 'Email address is required.' };
    }
    return { isValid: true };
  }

  if (!VALIDATION_PATTERNS.EMAIL.test(email.trim())) {
    return { isValid: false, error: 'Invalid email address format.' };
  }

  return { isValid: true };
}

/**
 * Validate US state code
 * @param {string} state
 * @param {Object} options
 * @returns {{ isValid: boolean, error?: string }}
 */
export function validateStateCode(state, options = {}) {
  const { required = false } = options;

  if (!state || state.trim().length === 0) {
    if (required) {
      return { isValid: false, error: 'State is required.' };
    }
    return { isValid: true };
  }

  if (!VALIDATION_PATTERNS.STATE_CODE.test(state.trim())) {
    return { isValid: false, error: 'State should be a 2-letter abbreviation (e.g., NC).' };
  }

  return { isValid: true };
}

/**
 * Validate US zipcode
 * @param {string} zipcode
 * @param {Object} options
 * @returns {{ isValid: boolean, error?: string }}
 */
export function validateZipcode(zipcode, options = {}) {
  const { required = false } = options;

  if (!zipcode || zipcode.trim().length === 0) {
    if (required) {
      return { isValid: false, error: 'Zipcode is required.' };
    }
    return { isValid: true };
  }

  if (!VALIDATION_PATTERNS.ZIPCODE.test(zipcode.trim())) {
    return {
      isValid: false,
      error: 'Zipcode must be in 5-digit or 9-digit format (e.g., 12345 or 12345-6789).',
    };
  }

  return { isValid: true };
}

/**
 * Validate a name field
 * @param {string} name
 * @param {Object} options
 * @returns {{ isValid: boolean, error?: string }}
 */
export function validateName(name, options = {}) {
  const { required = false, fieldName = 'Name' } = options;

  if (!name || name.trim().length === 0) {
    if (required) {
      return { isValid: false, error: `${fieldName} is required.` };
    }
    return { isValid: true };
  }

  return { isValid: true };
}

/**
 * Check if a date is a valid calendar date
 * @param {number} month - 1-12
 * @param {number} day - 1-31
 * @param {number} year
 * @returns {boolean}
 */
function isValidCalendarDate(month, day, year) {
  const date = new Date(year, month - 1, day);
  return (
    date.getFullYear() === year &&
    date.getMonth() === month - 1 &&
    date.getDate() === day
  );
}

/**
 * Validate a date (birthday or anniversary)
 * @param {Object} parts
 * @param {string|number} [parts.month]
 * @param {string|number} [parts.day]
 * @param {string|number} [parts.year]
 * @param {Object} options
 * @param {string} [options.dateType='date']
 * @param {number} [options.futureYearsAllowed=5]
 * @returns {{ isValid: boolean, error?: string }}
 */
export function validateDate(parts, options = {}) {
  const {
    dateType = 'date',
    futureYearsAllowed = 5,
  } = options;

  const monthStr = parts.month ? String(parts.month).trim() : '';
  const dayStr = parts.day ? String(parts.day).trim() : '';
  const yearStr = parts.year ? String(parts.year).trim() : '';

  // All empty - valid
  if (!monthStr && !dayStr && !yearStr) {
    return { isValid: true };
  }

  // Partial date - both month and day required
  if ((monthStr && !dayStr) || (!monthStr && dayStr)) {
    return {
      isValid: false,
      error: `If selecting a ${dateType} month or day, both are required.`,
    };
  }

  // Year without month/day
  if (yearStr && (!monthStr || !dayStr)) {
    return {
      isValid: false,
      error: `If selecting a ${dateType} year, month and day are also required.`,
    };
  }

  // Validate month range
  const monthNum = parseInt(monthStr, 10);
  if (isNaN(monthNum) || monthNum < 1 || monthNum > 12) {
    return { isValid: false, error: 'Invalid month value.' };
  }

  // Validate day range
  const dayNum = parseInt(dayStr, 10);
  if (isNaN(dayNum) || dayNum < 1 || dayNum > 31) {
    return { isValid: false, error: 'Invalid day value.' };
  }

  // Validate year if provided
  let yearNum = 2000; // default for calendar validation
  if (yearStr) {
    yearNum = parseInt(yearStr, 10);
    const maxYear = YEAR_VALIDATION.getMaxYear(futureYearsAllowed);
    if (isNaN(yearNum) || yearNum < YEAR_VALIDATION.MIN_YEAR || yearNum > maxYear) {
      return {
        isValid: false,
        error: `Year must be between ${YEAR_VALIDATION.MIN_YEAR} and ${maxYear}.`,
      };
    }
  }

  // Validate it's a real calendar date
  if (!isValidCalendarDate(monthNum, dayNum, yearNum)) {
    const dateStr = yearStr
      ? `${monthNum}/${dayNum}/${yearNum}`
      : `${monthNum}/${dayNum}`;
    return {
      isValid: false,
      error: `${dateStr} is not a valid calendar date.`,
    };
  }

  return { isValid: true };
}

/**
 * Validate a birthday (allows more future years)
 * @param {Object} parts
 * @returns {{ isValid: boolean, error?: string }}
 */
export function validateBirthday(parts) {
  return validateDate(parts, { dateType: 'birthday', futureYearsAllowed: 10 });
}

/**
 * Validate an anniversary
 * @param {Object} parts
 * @returns {{ isValid: boolean, error?: string }}
 */
export function validateAnniversary(parts) {
  return validateDate(parts, { dateType: 'anniversary', futureYearsAllowed: 5 });
}

/**
 * Validate family form data
 * @param {Object} data
 * @returns {{ isValid: boolean, errors: Object }}
 */
export function validateFamilyForm(data) {
  const errors = {};

  // Surname is required
  const surnameResult = validateName(data.surname, { required: true, fieldName: 'Family Surname' });
  if (!surnameResult.isValid) {
    errors.surname = surnameResult.error;
  }

  // Phone is optional but must be valid format
  const phoneResult = validatePhone(data.phone);
  if (!phoneResult.isValid) {
    errors.phone = phoneResult.error;
  }

  // State is optional but must be 2 letters
  const stateResult = validateStateCode(data.state);
  if (!stateResult.isValid) {
    errors.state = stateResult.error;
  }

  // Zipcode is optional but must be valid format
  const zipcodeResult = validateZipcode(data.zipcode);
  if (!zipcodeResult.isValid) {
    errors.zipcode = zipcodeResult.error;
  }

  // Anniversary is optional but must be valid date
  if (data.anniversaryMonth || data.anniversaryDay || data.anniversaryYear) {
    const anniversaryResult = validateAnniversary({
      month: data.anniversaryMonth,
      day: data.anniversaryDay,
      year: data.anniversaryYear,
    });
    if (!anniversaryResult.isValid) {
      errors.anniversary = anniversaryResult.error;
    }
  }

  return {
    isValid: Object.keys(errors).length === 0,
    errors,
  };
}

/**
 * Validate member form data
 * @param {Object} data
 * @returns {{ isValid: boolean, errors: Object }}
 */
export function validateMemberForm(data) {
  const errors = {};

  // Name is required
  const nameResult = validateName(data.name, { required: true, fieldName: 'Member name' });
  if (!nameResult.isValid) {
    errors.name = nameResult.error;
  }

  // Email is optional but must be valid format
  const emailResult = validateEmail(data.email);
  if (!emailResult.isValid) {
    errors.email = emailResult.error;
  }

  // Phone is optional but must be valid format
  const phoneResult = validatePhone(data.cellphone);
  if (!phoneResult.isValid) {
    errors.cellphone = phoneResult.error;
  }

  // Birthday is optional but must be valid date
  if (data.birthdayMonth || data.birthdayDay || data.birthdayYear) {
    const birthdayResult = validateBirthday({
      month: data.birthdayMonth,
      day: data.birthdayDay,
      year: data.birthdayYear,
    });
    if (!birthdayResult.isValid) {
      errors.birthday = birthdayResult.error;
    }
  }

  return {
    isValid: Object.keys(errors).length === 0,
    errors,
  };
}

// Export all validators
export default {
  cleanPhone,
  validatePhone,
  validateEmail,
  validateStateCode,
  validateZipcode,
  validateName,
  validateDate,
  validateBirthday,
  validateAnniversary,
  validateFamilyForm,
  validateMemberForm,
};
